import {
  Card,
  CardContent,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useOperationsDashboard } from '@/api/hooks'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
  MetricCard,
  PageHeader,
  StatusChip,
} from '@/components/common'

export function OperationsDashboardPage() {
  const { data, isLoading, error } = useOperationsDashboard()

  return (
    <>
      <PageHeader
        title="Operations dashboard"
        subtitle="What is coming up, and what is still unresolved"
      />
      <ErrorBanner error={error} />
      {isLoading && <InlineSpinner />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <MetricCard label="Events next 7 days" value={formatNumber(data.eventsNext7Days)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Next 15 days" value={formatNumber(data.eventsNext15Days)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Next 30 days" value={formatNumber(data.eventsNext30Days)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Awaiting advance"
                value={formatNumber(data.pendingCustomerConfirmations)}
                tone={data.pendingCustomerConfirmations > 0 ? 'warning' : 'default'}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <MetricCard
                label="Kitchen requests unapproved"
                value={formatNumber(data.kitchenRequestsNotApproved)}
                tone={data.kitchenRequestsNotApproved > 0 ? 'warning' : 'success'}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Staff shortages"
                value={formatNumber(data.staffShortages)}
                tone={data.staffShortages > 0 ? 'error' : 'success'}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Vehicle shortages"
                value={formatNumber(data.vehicleShortages)}
                tone={data.vehicleShortages > 0 ? 'error' : 'success'}
              />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Upcoming events
              </Typography>
              {data.upcomingEvents.length === 0 ? (
                <EmptyState
                  message="Nothing scheduled in the next 30 days."
                  hint="Confirmed orders appear here as soon as a quotation converts."
                />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Guests</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.upcomingEvents.map((event) => (
                      <TableRow key={event.orderId} hover>
                        <TableCell>{formatDate(event.eventDate)}</TableCell>
                        <TableCell>
                          <Link component={RouterLink} to={`/orders/${event.orderId}`} underline="hover">
                            {event.orderNumber}
                          </Link>
                        </TableCell>
                        <TableCell>{event.customerName}</TableCell>
                        <TableCell>{humanise(event.serviceType)}</TableCell>
                        <TableCell align="right">{formatNumber(event.guestCount)}</TableCell>
                        <TableCell>
                          <StatusChip status={event.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={event.balanceAmount > 0 ? 'warning.main' : 'text.secondary'}
                          >
                            {formatMoney(event.balanceAmount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </>
  )
}
